// Admin: generate + manage wallet recharge cards.
import { useEffect, useState, useCallback } from "react";
import api, { extractError } from "@/api/client";
import { X, CreditCard, Plus, Copy, Download, RefreshCw, Ban, Check, Printer } from "lucide-react";

const DENOMS = [5, 10, 20, 50, 100];

function fmtDate(iso) { try { return new Date(iso).toLocaleString("ar", { dateStyle: "short", timeStyle: "short" }); } catch { return ""; } }

export default function AdminCardsModal({ onClose }) {
  const [tab, setTab] = useState("generate");
  const [denom, setDenom] = useState(10);
  const [count, setCount] = useState(10);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [batch, setBatch] = useState(null); // freshly generated batch
  const [filterDenom, setFilterDenom] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [cards, setCards] = useState([]);
  const [summary, setSummary] = useState({});
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterDenom) params.set("denomination", filterDenom);
      if (filterStatus) params.set("status", filterStatus);
      const { data } = await api.get(`/admin/wallet-cards?${params.toString()}`);
      setCards(data.cards || []);
      setSummary(data.summary || {});
    } catch (e) { console.warn("cards load", e); }
  }, [filterDenom, filterStatus]);
  useEffect(() => { if (tab === "list") load(); }, [tab, load]);

  const generate = async () => {
    setBusy(true); setErr(""); setBatch(null);
    try {
      const { data } = await api.post("/admin/wallet-cards/generate", { denomination: denom, count: parseInt(count, 10), note });
      setBatch(data);
      setNote("");
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const copyAll = () => {
    if (!batch?.cards) return;
    const txt = batch.cards.map((c) => `${c.serial}\t${c.code}\t${c.amount} د.ل`).join("\n");
    navigator.clipboard?.writeText(txt);
  };

  const downloadCSV = () => {
    if (!batch?.cards) return;
    const rows = [["Serial", "Code", "Amount (LYD)", "Batch", "Note"]];
    batch.cards.forEach((c) => rows.push([c.serial, c.code, c.amount, batch.batch_id, batch.note || ""]));
    const csv = "\uFEFF" + rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `talabya-cards-${batch.batch_id}-${denom}LYD.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  // Print all freshly-generated cards on a 58mm thermal printer.
  // Each card becomes its own receipt slip; the printer cuts paper between them
  // (each slip lives in a page-break block).
  const printThermal58 = () => {
    if (!batch?.cards?.length) return;
    // Format the 13-digit code with a space every 4 chars for readability
    const fmtCode = (c) => (c || "").replace(/(\d{4})(?=\d)/g, "$1 ");
    const dateStr = new Date().toLocaleString("ar-LY", { dateStyle: "short", timeStyle: "short" });
    // Absolute URL so the print pop-up (about:blank origin) can still fetch the icon.
    const logoUrl = `${window.location.origin}/icon-192.png`;
    const slips = batch.cards.map((c, i) => `
      <section class="slip ${i === batch.cards.length - 1 ? "last" : ""}">
        <div class="head">
          <img class="logo" src="${logoUrl}" alt="Talabya" onerror="this.style.display='none'"/>
          <div class="brand-block">
            <div class="brand">طلبية</div>
            <div class="sub">كرت شحن محفظة</div>
          </div>
        </div>
        <div class="amount">${c.amount} د.ل</div>
        <div class="code" dir="ltr">${fmtCode(c.code)}</div>
        <div class="meta">التسلسلي <b>${c.serial}</b> · الدفعة <b>${batch.batch_id}</b></div>
        ${batch.note ? `<div class="meta">${batch.note}</div>` : ""}
        <div class="divider"></div>
        <div class="hint">استرداد الكرت من: طلبية ← المحفظة</div>
        <div class="date">${dateStr}</div>
      </section>`).join("");

    // 58mm thermal roll: printable width is ~48mm after driver margins. We keep
    // the slip 52mm wide with 3mm padding so text never clips at the edges.
    const html = `<!doctype html>
<html lang="ar" dir="rtl">
<head><meta charset="utf-8"/>
<title>طباعة كروت شحن</title>
<style>
  @page { size: 58mm auto; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; color: #000; font-family: 'Tajawal','Cairo','Segoe UI',Arial,sans-serif; }
  .slip { width: 52mm; padding: 1.5mm 2.5mm; page-break-after: always; break-after: page; text-align: center; }
  .slip.last { page-break-after: auto; }
  .head { display: flex; align-items: center; justify-content: center; gap: 1.5mm; margin-bottom: 0.8mm; }
  .logo { width: 7mm; height: 7mm; object-fit: contain; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .brand-block { text-align: right; }
  .brand { font-weight: 900; font-size: 11pt; line-height: 1; }
  .sub { font-size: 5.5pt; opacity: 0.8; margin-top: 0.3mm; }
  .divider { border-top: 1.5px dashed #000; margin: 0.8mm 0; }
  .amount { font-weight: 900; font-size: 12pt; margin: 0.5mm 0; }
  .code { font-family: 'Courier New', monospace; font-weight: 900; font-size: 11pt; letter-spacing: 0.5pt; margin: 0.5mm 0; padding: 0.5mm 0; border: 1px solid #000; }
  .meta { font-size: 6pt; margin: 0.2mm 0; }
  .hint { font-size: 5.5pt; margin: 0.5mm 0 0.3mm; line-height: 1.2; }
  .date { font-size: 5.5pt; opacity: 0.7; margin-top: 0.4mm; }
  @media screen {
    body { background: #eee; padding: 12px; }
    .slip { background: #fff; margin: 0 auto 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.15); }
  }
</style>
</head>
<body>${slips}
<script>
  // Wait until every <img> settles (loaded OR failed) before triggering print
  // so the thermal driver captures the logo bitmap on the first page.
  function waitForImages() {
    var imgs = Array.prototype.slice.call(document.images);
    if (!imgs.length) return Promise.resolve();
    return Promise.all(imgs.map(function (img) {
      if (img.complete) return Promise.resolve();
      return new Promise(function (r) { img.onload = img.onerror = r; });
    }));
  }
  window.onload = function () {
    waitForImages().then(function () {
      setTimeout(function () { window.print(); }, 150);
    });
  };
  window.onafterprint = function () { setTimeout(function () { window.close(); }, 300); };
</script>
</body></html>`;
    const w = window.open("", "_blank", "width=360,height=720");
    if (!w) { alert("متصفحك يمنع النوافذ المنبثقة — اسمح بها للطباعة."); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  const revoke = async (id) => {
    if (!window.confirm("إلغاء هذا الكرت؟ سيصبح غير قابل للاستخدام.")) return;
    try {
      await api.post(`/admin/wallet-cards/${id}/revoke`);
      load();
    } catch (e) { alert(extractError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" data-testid="admin-cards-modal">
      <div className="bg-white w-full max-w-3xl max-h-[92vh] rounded-3xl talabya-shadow-soft overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-primary-brand to-blue-500 text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><CreditCard size={18}/><div className="font-black text-lg" style={{fontFamily:"Cairo"}}>كروت شحن المحفظة</div></div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center" data-testid="cards-close"><X size={16}/></button>
        </div>
        <div className="flex border-b border-[var(--border)]">
          <button onClick={() => setTab("generate")} className={`flex-1 py-3 text-sm font-bold ${tab === "generate" ? "border-b-2 border-primary-brand text-primary-brand bg-blue-50" : "text-[var(--text-secondary)]"}`} data-testid="tab-gen">توليد كروت جديدة</button>
          <button onClick={() => setTab("list")} className={`flex-1 py-3 text-sm font-bold ${tab === "list" ? "border-b-2 border-primary-brand text-primary-brand bg-blue-50" : "text-[var(--text-secondary)]"}`} data-testid="tab-list">قائمة الكروت</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {tab === "generate" && !batch && (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-bold">الفئة (د.ل)</label>
                <div className="grid grid-cols-5 gap-2 mt-2">
                  {DENOMS.map((d) => (
                    <button key={d} onClick={() => setDenom(d)}
                      className={`py-3 rounded-xl font-black text-lg border-2 ${denom === d ? "border-primary-brand bg-primary-light text-primary-brand" : "border-[var(--border)] bg-white text-[var(--text-secondary)]"}`}
                      data-testid={`denom-${d}`}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-bold">عدد الكروت (1 - 500)</label>
                <input type="number" min="1" max="500" value={count} onChange={(e) => setCount(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border)] focus:border-primary-brand focus:outline-none"
                  data-testid="cards-count"/>
              </div>
              <div>
                <label className="text-sm font-bold">ملاحظة (اختياري)</label>
                <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border)] focus:border-primary-brand focus:outline-none text-right"
                  placeholder="مثلاً: دفعة رمضان 2026"
                  data-testid="cards-note"/>
              </div>
              {err && <div className="text-sm bg-red-50 text-red-600 px-3 py-2 rounded-lg" data-testid="cards-err">{err}</div>}
              <div className="text-xs bg-blue-50 text-primary-brand p-3 rounded-lg">
                ⚠ الكودات ستُعرض <strong>مرة واحدة فقط</strong> بعد التوليد — احفظها فوراً (نسخ / تحميل CSV) قبل الإغلاق.
              </div>
              <button onClick={generate} disabled={busy}
                className="w-full py-3 rounded-xl bg-primary-brand text-white font-black text-base disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="generate-btn">
                <Plus size={16}/> {busy ? "جاري التوليد..." : `توليد ${count} كرت بفئة ${denom} د.ل`}
              </button>
            </div>
          )}

          {tab === "generate" && batch && (
            <div className="space-y-3" data-testid="batch-result">
              <div className="p-4 rounded-2xl bg-green-50 border border-green-200 text-sm">
                <div className="font-black text-green-800">✅ تم توليد {batch.count} كرت بفئة {batch.denomination} د.ل</div>
                <div className="text-xs mt-1 text-green-700">دفعة: <span className="font-mono font-bold">{batch.batch_id}</span></div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={copyAll} className="flex-1 min-w-[100px] py-2 rounded-lg bg-white border border-[var(--border)] text-sm font-bold flex items-center justify-center gap-1 hover:bg-blue-50" data-testid="copy-all"><Copy size={12}/> نسخ الكل</button>
                <button onClick={downloadCSV} className="flex-1 min-w-[100px] py-2 rounded-lg bg-white border border-[var(--border)] text-sm font-bold flex items-center justify-center gap-1 hover:bg-blue-50" data-testid="download-csv"><Download size={12}/> تحميل CSV</button>
                <button onClick={printThermal58} className="flex-1 min-w-[130px] py-2 rounded-lg bg-primary-brand text-white text-sm font-black flex items-center justify-center gap-1 hover:opacity-90" data-testid="print-thermal-58" title="طباعة على طابعة حرارية 58mm">
                  <Printer size={12}/> طباعة 58mm
                </button>
                <button onClick={() => setBatch(null)} className="flex-1 min-w-[100px] py-2 rounded-lg bg-gray-100 text-sm font-bold" data-testid="new-batch">دفعة جديدة</button>
              </div>
              <div className="max-h-72 overflow-y-auto border border-[var(--border)] rounded-xl">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--surface-alt)] sticky top-0"><tr><th className="p-2 text-right">التسلسلي</th><th className="p-2 text-center">الكود (13 رقم)</th><th className="p-2 text-left">القيمة</th></tr></thead>
                  <tbody>
                    {batch.cards.map((c) => (
                      <tr key={c.serial} className="border-t border-[var(--border)]" data-testid={`gen-${c.serial}`}>
                        <td className="p-2 font-mono text-xs">{c.serial}</td>
                        <td className="p-2 text-center font-mono font-black text-primary-brand tracking-wider" dir="ltr">{c.code}</td>
                        <td className="p-2 text-left font-bold">{c.amount} د.ل</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "list" && (
            <div className="space-y-3">
              <div className="grid grid-cols-5 gap-2" data-testid="cards-summary">
                {DENOMS.map((d) => {
                  const s = summary[String(d)] || {};
                  return (
                    <div key={d} className="p-2 rounded-lg bg-[var(--surface-alt)] text-center" data-testid={`sum-${d}`}>
                      <div className="text-xs font-bold">{d} د.ل</div>
                      <div className="text-[10px] text-green-600">{s.active || 0} متاح</div>
                      <div className="text-[10px] text-gray-500">{s.redeemed || 0} مُستخدم</div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 items-center">
                <select value={filterDenom} onChange={(e) => setFilterDenom(e.target.value)} className="px-2 py-1.5 rounded-lg border border-[var(--border)] text-sm" data-testid="filter-denom">
                  <option value="">كل الفئات</option>
                  {DENOMS.map((d) => <option key={d} value={d}>{d} د.ل</option>)}
                </select>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-2 py-1.5 rounded-lg border border-[var(--border)] text-sm" data-testid="filter-status">
                  <option value="">كل الحالات</option>
                  <option value="active">متاحة</option>
                  <option value="redeemed">مُستخدمة</option>
                  <option value="revoked">مُلغاة</option>
                </select>
                <button onClick={load} className="px-3 py-1.5 rounded-lg bg-white border border-[var(--border)] text-sm font-bold flex items-center gap-1" data-testid="refresh-list"><RefreshCw size={12}/></button>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {cards.length === 0 ? (
                  <div className="text-center text-sm text-[var(--text-secondary)] py-8">لا يوجد كروت مطابقة</div>
                ) : (
                  <table className="w-full text-xs" data-testid="cards-table">
                    <thead className="bg-[var(--surface-alt)] sticky top-0"><tr><th className="p-2 text-right">التسلسلي</th><th className="p-2">الكود</th><th className="p-2">القيمة</th><th className="p-2">الحالة</th><th className="p-2">المُستخدم</th><th className="p-2"></th></tr></thead>
                    <tbody>
                      {cards.map((c) => (
                        <tr key={c.id} className="border-t border-[var(--border)]" data-testid={`card-${c.id}`}>
                          <td className="p-2 font-mono">{c.serial}</td>
                          <td className="p-2 font-mono" dir="ltr">{c.masked_code}</td>
                          <td className="p-2 font-bold">{c.amount}</td>
                          <td className="p-2">
                            {c.status === "active" && <span className="text-green-600">متاح</span>}
                            {c.status === "redeemed" && <span className="text-blue-600">مُستخدم</span>}
                            {c.status === "revoked" && <span className="text-red-600">مُلغى</span>}
                          </td>
                          <td className="p-2 text-[10px]">
                            {c.redeemed_by_name && <div className="font-bold">{c.redeemed_by_name}</div>}
                            {c.redeemed_at && <div className="text-gray-500">{fmtDate(c.redeemed_at)}</div>}
                          </td>
                          <td className="p-2">
                            {c.status === "active" && (
                              <button onClick={() => revoke(c.id)} className="text-red-600 hover:bg-red-50 p-1 rounded" title="إلغاء" data-testid={`revoke-${c.id}`}><Ban size={12}/></button>
                            )}
                            {c.status === "redeemed" && <Check size={12} className="text-blue-600"/>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
